import { Alert, Button, Card, Grid, Space, Table, Tag, Typography } from "antd";
import type { TableColumnsType } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { formatDate } from "../app/classes.ts";
import { formatSchoolDateTime } from "../app/time.ts";
import { formatPercent } from "./AttendanceSummaryTable.tsx";
import {
  useAttendancePatterns,
  type FullAttendanceClassFinding,
  type LateMarkedLessonFinding,
  type ReportRange,
  type TeacherWithoutLateOrAbsentFinding,
  type UnmarkedLessonFinding,
} from "../app/reports.ts";

/** Findings are bounded by the API (at most 100 per kind); the tables page through them in the browser. */
const FINDINGS_PAGE_SIZE = 10;

interface Props {
  range: ReportRange;
  enabled: boolean;
}

interface SectionProps<T> {
  title: string;
  description: string;
  rows: T[];
  total: number;
  loading: boolean;
  rowKey: keyof T & string;
  columns: TableColumnsType<T>;
  scrollX: number;
}

function FindingSection<T extends object>({ title, description, rows, total, loading, rowKey, columns, scrollX }: SectionProps<T>) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  return (
    <Card
      styles={{ title: { whiteSpace: "normal", paddingBlock: 12 } }}
      title={
        <Space size={8} wrap>
          <span>{title}</span>
          <Tag color={total > 0 ? "gold" : "default"} style={{ marginInlineEnd: 0 }}>
            {t("reports.patterns.countBadge", { count: total })}
          </Tag>
        </Space>
      }
    >
      <Space orientation="vertical" size="small" style={{ width: "100%" }}>
        <Typography.Text type="secondary">{description}</Typography.Text>
        {total > rows.length && <Typography.Text type="warning">{t("reports.patterns.truncated", { shown: rows.length, total })}</Typography.Text>}
        <Table<T>
          rowKey={rowKey}
          size={screens.md ? "middle" : "small"}
          loading={loading}
          columns={columns}
          dataSource={rows}
          locale={{ emptyText: t("reports.patterns.none") }}
          scroll={{ x: scrollX }}
          pagination={{ pageSize: FINDINGS_PAGE_SIZE, hideOnSinglePage: true, showSizeChanger: false }}
        />
      </Space>
    </Card>
  );
}

/** The unusual-pattern report: review aids with their evidence, never verdicts. Thresholds come from the API's configuration. */
export function AttendancePatternsTab({ range, enabled }: Props) {
  const { t } = useTranslation();
  const query = useAttendancePatterns(range, enabled);
  const report = query.data;
  const loading = query.isPending || (query.isFetching && query.isPlaceholderData);

  const dates = (first: string, last: string) => t("reports.patterns.dateRange", { from: formatDate(first), to: formatDate(last) });
  const lessonLabel = (lesson: { date: string; period: number | string; subjectName: string }) =>
    t("reports.patterns.lessonLabel", { date: formatDate(lesson.date), period: Number(lesson.period), subject: lesson.subjectName });
  const teacherCell = (name: string | null | undefined) =>
    name ? name : <Typography.Text type="secondary">{t("reports.attendance.noTeacher")}</Typography.Text>;

  const classColumns: TableColumnsType<FullAttendanceClassFinding> = [
    {
      title: t("reports.patterns.columns.class"),
      key: "class",
      render: (_: unknown, row: FullAttendanceClassFinding) => (
        <Link to={`/academic/classes/${row.classId}`}>
          <Typography.Text strong>{row.className}</Typography.Text>
        </Link>
      ),
    },
    { title: t("reports.patterns.columns.year"), dataIndex: "academicYearLabel", key: "year" },
    { title: t("reports.patterns.columns.markedLessons"), key: "lessons", align: "right", render: (_: unknown, row: FullAttendanceClassFinding) => Number(row.markedLessons) },
    { title: t("reports.patterns.columns.marks"), key: "marks", align: "right", render: (_: unknown, row: FullAttendanceClassFinding) => Number(row.marks) },
    { title: t("reports.patterns.columns.absences"), key: "absences", align: "right", render: (_: unknown, row: FullAttendanceClassFinding) => Number(row.absences) },
    {
      title: t("reports.patterns.columns.attendedPercent"),
      key: "attended",
      align: "right",
      render: (_: unknown, row: FullAttendanceClassFinding) => formatPercent(row.attendedPercent),
    },
    { title: t("reports.patterns.columns.dates"), key: "dates", render: (_: unknown, row: FullAttendanceClassFinding) => dates(row.firstDate, row.lastDate) },
  ];
  const fullAttendancePercent = Number(report?.thresholds.fullAttendanceMinPercent ?? 0);

  const teacherColumns: TableColumnsType<TeacherWithoutLateOrAbsentFinding> = [
    { title: t("reports.patterns.columns.teacher"), key: "teacher", render: (_: unknown, row: TeacherWithoutLateOrAbsentFinding) => <Typography.Text strong>{row.teacherName}</Typography.Text> },
    { title: t("reports.patterns.columns.markedLessons"), key: "lessons", align: "right", render: (_: unknown, row: TeacherWithoutLateOrAbsentFinding) => Number(row.markedLessons) },
    { title: t("reports.patterns.columns.marks"), key: "marks", align: "right", render: (_: unknown, row: TeacherWithoutLateOrAbsentFinding) => Number(row.marks) },
    { title: t("reports.patterns.columns.dates"), key: "dates", render: (_: unknown, row: TeacherWithoutLateOrAbsentFinding) => dates(row.firstDate, row.lastDate) },
  ];

  const lateColumns: TableColumnsType<LateMarkedLessonFinding> = [
    { title: t("reports.patterns.columns.lesson"), key: "lesson", render: (_: unknown, row: LateMarkedLessonFinding) => lessonLabel(row) },
    { title: t("reports.patterns.columns.class"), dataIndex: "className", key: "class" },
    { title: t("reports.patterns.columns.teacher"), key: "teacher", render: (_: unknown, row: LateMarkedLessonFinding) => teacherCell(row.teacherName) },
    { title: t("reports.patterns.columns.endedAt"), key: "ended", render: (_: unknown, row: LateMarkedLessonFinding) => formatSchoolDateTime(row.endedAt) },
    { title: t("reports.patterns.columns.firstMarkedAt"), key: "marked", render: (_: unknown, row: LateMarkedLessonFinding) => formatSchoolDateTime(row.firstMarkedAt) },
    {
      title: t("reports.patterns.columns.delay"),
      key: "delay",
      align: "right",
      render: (_: unknown, row: LateMarkedLessonFinding) => <Typography.Text type="warning">{t("reports.patterns.hours", { count: Number(row.hoursAfterEnd) })}</Typography.Text>,
    },
  ];

  const unmarkedColumns: TableColumnsType<UnmarkedLessonFinding> = [
    { title: t("reports.patterns.columns.lesson"), key: "lesson", render: (_: unknown, row: UnmarkedLessonFinding) => lessonLabel(row) },
    { title: t("reports.patterns.columns.class"), dataIndex: "className", key: "class" },
    { title: t("reports.patterns.columns.teacher"), key: "teacher", render: (_: unknown, row: UnmarkedLessonFinding) => teacherCell(row.teacherName) },
    {
      title: t("reports.patterns.columns.marked"),
      key: "marked",
      align: "right",
      render: (_: unknown, row: UnmarkedLessonFinding) => t("reports.patterns.markedOf", { marked: Number(row.markedCount), roster: Number(row.rosterCount) }),
    },
    { title: t("reports.patterns.columns.deadline"), key: "deadline", render: (_: unknown, row: UnmarkedLessonFinding) => formatSchoolDateTime(row.deadline) },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      {query.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>} />}
      <Alert type="info" showIcon title={t("reports.patterns.intro")} />
      <FindingSection<FullAttendanceClassFinding>
        title={t("reports.patterns.fullAttendance.title")}
        description={t("reports.patterns.fullAttendance.description", {
          count: Number(report?.thresholds.fullAttendanceMinLessons ?? 0),
          percent: fullAttendancePercent,
        })}
        rows={report?.fullAttendanceClasses.items ?? []}
        total={Number(report?.fullAttendanceClasses.totalCount ?? 0)}
        loading={loading}
        rowKey="classId"
        columns={classColumns}
        scrollX={800}
      />
      <FindingSection<TeacherWithoutLateOrAbsentFinding>
        title={t("reports.patterns.noLateOrAbsent.title")}
        description={t("reports.patterns.noLateOrAbsent.description", { count: Number(report?.thresholds.noLateOrAbsentMinLessons ?? 0) })}
        rows={report?.teachersWithoutLateOrAbsent.items ?? []}
        total={Number(report?.teachersWithoutLateOrAbsent.totalCount ?? 0)}
        loading={loading}
        rowKey="teacherId"
        columns={teacherColumns}
        scrollX={560}
      />
      <FindingSection<LateMarkedLessonFinding>
        title={t("reports.patterns.lateMarked.title")}
        description={t("reports.patterns.lateMarked.description", { hours: Number(report?.thresholds.lateMarkingHours ?? 0) })}
        rows={report?.lateMarkedLessons.items ?? []}
        total={Number(report?.lateMarkedLessons.totalCount ?? 0)}
        loading={loading}
        rowKey="lessonId"
        columns={lateColumns}
        scrollX={900}
      />
      <FindingSection<UnmarkedLessonFinding>
        title={t("reports.patterns.unmarked.title")}
        description={t("reports.patterns.unmarked.description")}
        rows={report?.unmarkedLessons.items ?? []}
        total={Number(report?.unmarkedLessons.totalCount ?? 0)}
        loading={loading}
        rowKey="lessonId"
        columns={unmarkedColumns}
        scrollX={800}
      />
    </Space>
  );
}
